import { Divider, MantineTheme, Stack, Text, Tooltip } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import itemsIconBlack from '@/assets/icons/items_black.svg'
import itemsIconWhite from '@/assets/icons/items_white.svg'
import useL1NS from '@/hooks/useL1NS'
import useLocalStorage from '@/hooks/useLocalStorage'
import useProfilePath from '@/hooks/useProfilePath'
import { getDefaultProfileImage } from '@/plugins/auth/AuthProvider/effects/functions'

import receivedIcon from '../../assets/icons/received_arrow_left.svg'
// SVGs
import sentIcon from '../../assets/icons/sent_arrow_right.svg'
import placeholder from '../../assets/webp_converted/imageOnload.webp'
import {
  AIRDROP_CONTRACT_ADDRESS,
  BRIDGE_AVAX_ERC20_REMOTE_ADDRESS,
  BRIDGE_L1_NATIVE_HOME_ADDRESS,
  CONTRACT_COLLECTOR_ADDRESS,
  FAUCET_ADDRESS,
  LL1_ADDRESS,
  NATIVE_CHAIN_TICKER,
  REWARDS_ADDRESS,
  RL1_ADDRESS,
  STAKE_ADDRESS,
} from '../../constants/blockchain'
import { Transfer } from '../../schemas/types'
import { baseTxUrl, SimpleAddressDisplay } from '../../utils/table'
import {
  formattedDateTime,
  formatValueToSuffix,
  ipfsToUrl,
  itemNameWithId,
  StoredTheme,
  truncateString,
  writeTransferType,
} from '../../utils/utils'
import UserDetails from '../Details/UserDetails/UserDetails'
// Files
import IconWrapper from '../MediaWrappers/IconWrapper'
import { ImageCrop } from '../MediaWrappers/ImageCrop'
import { loadProfile } from '../Profile/metadata'
import TokenDisplay from '../Tokens/TokensDisplay'
import classes from './Activity.module.css'
import Status from './Status'

const ItemsTooltip: React.FC<{ name: string; isMobile: boolean; transfer: Transfer }> = ({
  name,
  isMobile,
  transfer,
}) => {
  const navigate = useNavigate()
  return isMobile ? (
    name.length < 12 ? (
      <Text
        className={classes.text}
        onClick={() => {
          navigate(`/item/${transfer.token.id}`)
        }}
      >
        {name}
      </Text>
    ) : (
      <Tooltip withArrow label={name} position="top-start" offset={10}>
        <Text
          className={classes.text}
          onClick={() => {
            navigate(`/item/${transfer.token.id}`)
          }}
        >
          {truncateString(name)}
        </Text>
      </Tooltip>
    )
  ) : name.length < 15 ? (
    <Text
      className={classes.text}
      onClick={() => {
        navigate(`/item/${transfer.token.id}`)
      }}
    >
      {name}
    </Text>
  ) : (
    <Tooltip withArrow label={name} position="top-start" offset={10}>
      <Text
        className={classes.text}
        onClick={() => {
          navigate(`/item/${transfer.token.id}`)
        }}
      >
        {truncateString(name, 15)}
      </Text>
    </Tooltip>
  )
}

export const ProfileImageTx: React.FC<{
  address: string
  iconSize: string
  transfer: Transfer | 'L1'
}> = ({ address, iconSize, transfer }) => {
  const navigate = useNavigate()
  // default profile images
  const [userTheme] = useLocalStorage<StoredTheme>('userTheme')
  const defaultPfp = useMemo(() => getDefaultProfileImage(userTheme?.isLightMode), [userTheme])
  const [profileImage, setProfileImage] = useState<string>(defaultPfp)
  const { getL1nsProfile } = useL1NS()
  const profilePath = useProfilePath(address as `0x${string}`)

  useEffect(() => {
    if (address) {
      getL1nsProfile(address).then(profileUrl => {
        try {
          loadProfile(profileUrl as string).then(profile => {
            if (profile) {
              setProfileImage(ipfsToUrl(profile.image))
            } else {
              setProfileImage(defaultPfp)
            }
          })
        } catch {
          setProfileImage(defaultPfp)
        }
      })
    }
  }, [address])

  return profileImage === 'none' ? (
    <TokenDisplay
      token={transfer === 'L1' ? NATIVE_CHAIN_TICKER : `${transfer.token.collection.symbol}`}
      onlyIcon
      size={iconSize}
    />
  ) : (
    <Stack
      onClick={() => {
        navigate(profilePath)
      }}
    >
      <img
        src={profileImage || defaultPfp}
        style={{
          width: iconSize,
          height: iconSize,
          borderRadius: '8px',
          cursor: 'pointer',
        }}
        alt="profileimage"
      />
    </Stack>
  )
}

const parseAsset = (transfer: Transfer, isMobile: boolean = false): JSX.Element => {
  // Native
  if (transfer.token === null) {
    const nativeValue = parseFloat(transfer.amount) / 1e18

    return (
      <Tooltip
        withArrow
        label={`${nativeValue} ${NATIVE_CHAIN_TICKER}`}
        position="top-start"
        offset={10}
      >
        <Text
          className={classes.fitContentText}
          onClick={() => {
            window.open(`${baseTxUrl}${transfer.txnHash}`, '_blank')
          }}
        >
          {formatValueToSuffix(nativeValue)} {NATIVE_CHAIN_TICKER}
        </Text>
      </Tooltip>
    )
  }

  // ERC20
  if (
    transfer.token.collection.collectionType === 'ERC20' &&
    transfer.token.collection.decimals !== null
  ) {
    const erc20Value = parseFloat(transfer.amount) / 10 ** transfer.token.collection.decimals

    return (
      <Tooltip
        withArrow
        label={`${erc20Value} ${transfer.token.collection.symbol}`}
        position="top-start"
        offset={10}
      >
        <Text
          className={classes.fitContentText}
          onClick={() => {
            window.open(`${baseTxUrl}${transfer.txnHash}`, '_blank')
          }}
        >
          {formatValueToSuffix(erc20Value)} {transfer.token.collection.symbol}
        </Text>
      </Tooltip>
    )
  }

  // Item
  if (transfer.token.metadata) {
    try {
      const { metadata } = transfer.token
      return (
        <ItemsTooltip
          name={itemNameWithId(metadata.name, transfer.token.nativeId)}
          isMobile={isMobile}
          transfer={transfer}
        />
      )
    } catch {
      if (transfer.token.collection.name) {
        return (
          <ItemsTooltip
            name={transfer.token.collection.name}
            isMobile={isMobile}
            transfer={transfer}
          />
        )
      }
    }
    return <Text style={{ fontFamily: 'monument-grotesk-bold', textAlign: 'left' }}>Item</Text>
  }

  if (transfer.token.collection.name) {
    return (
      <ItemsTooltip name={transfer.token.collection.name} isMobile={isMobile} transfer={transfer} />
    )
  }

  return <Text style={{ fontFamily: 'monument-grotesk-bold', textAlign: 'left' }}>Item</Text>
}

const parseAssetIcon = (transfer: Transfer): JSX.Element => {
  const iconSize = 40
  // Native
  if (transfer.token === null) {
    return <TokenDisplay token={NATIVE_CHAIN_TICKER} onlyIcon size={iconSize} />
  }

  // ERC20
  if (
    transfer.token.collection.collectionType === 'ERC20' &&
    transfer.token.collection.decimals !== null
  ) {
    return (
      <Text
        className={classes.fitContentText}
        onClick={() => {
          window.open(`${baseTxUrl}${transfer.txnHash}`, '_blank')
        }}
      >
        <TokenDisplay token={`${transfer.token.collection.symbol}`} onlyIcon size={iconSize} />
      </Text>
    )
  }

  // Item
  if (transfer.token.metadata) {
    try {
      const { metadata } = transfer.token
      return (
        <Link to={`/item/${transfer.token.id}`}>
          <ImageCrop
            imageUrl={metadata.image}
            data={metadata.cropInfo}
            width={iconSize}
            height={iconSize}
          />
        </Link>
      )
    } catch {
      // Noop
    }
  }

  // If we reach this point it means it's an item without any metadata or URI, so can't do anything
  // Use the placeholder image for no image
  return (
    <IconWrapper
      iconDark={placeholder}
      iconLight={placeholder}
      style={{
        width: iconSize,
        height: iconSize,
        borderRadius: '8px',
        cursor: 'pointer',
      }}
      loader
      size={`${iconSize}px`}
    />
  )
}

export const dataToRowTxMobile: (
  address: `0x${string}` | string,
  transfer: Transfer,
  prevTxHash: string,
  theme: MantineTheme
) => JSX.Element = (
  address: `0x${string}` | string,
  transfer: Transfer,
  prevTxHash: string,
  theme: MantineTheme
) => (
  <Stack key={`mobile-${transfer.txnHash}-${transfer.transferType}`}>
    <Stack
      key={`mobile-${transfer.txnHash}`}
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: '10px',
      }}
      onClick={() => {
        window.open(`${baseTxUrl}${transfer.txnHash}`, '_blank')
      }}
    >
      <Stack
        style={{
          flexDirection: 'row',
          width: '-webkit-fill-available',
          minHeight: '100%',
        }}
      >
        <Stack style={{ width: 'fit-content', gap: '0px', justifyContent: 'space-between' }}>
          <Stack style={{ flexDirection: 'row', gap: '5px' }}>
            <Status status={transfer.status || 0} />
            <Text style={{ fontFamily: 'monument-grotesk-bold', textAlign: 'left' }}>
              {parseAsset(transfer, true)}
            </Text>
          </Stack>
          <Stack
            key={transfer.timestamp}
            style={{
              flexDirection: 'row',
              gap: '5px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text key={transfer.txnHash} style={{ fontFamily: 'monument-grotesk-bold' }}>
              {writeTransferType(transfer)}
            </Text>
            {transfer?.token?.metadata && (
              <>
                <Text style={{ fontFamily: 'monument-grotesk-bold' }}>{transfer.amount}</Text>
                <IconWrapper
                  iconDark={itemsIconWhite}
                  iconLight={itemsIconBlack}
                  style={{ width: '12px', height: '12px' }}
                />
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack
        style={{
          textAlign: 'right',
          whiteSpace: 'nowrap',
          width: 'auto',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <Stack
          style={{ flexDirection: 'row' }}
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <IconWrapper
            iconDark={transfer.from === address ? sentIcon : receivedIcon}
            iconLight={transfer.from === address ? sentIcon : receivedIcon}
            style={{ padding: '1px' }}
          />
          <SimpleAddressDisplay
            address={
              transfer.transferType !== 'TRANSFER'
                ? transfer.token?.collection.id
                : transfer.from === address
                  ? transfer.to
                  : transfer.from
            }
          />
        </Stack>
        <Text
          style={{ fontSize: '14px', whiteSpace: 'nowrap', color: theme.colors.textAndIcon[0] }}
        >
          {formattedDateTime(transfer.timestamp)}
        </Text>
      </Stack>
    </Stack>
    {/* {prevTxHash !== transfer.txnHash && ( */}
    <Divider style={{ height: '1px', borderTopColor: theme.colors.splitter[0] }} />
    {/* )} */}
  </Stack>
)
/* -------------------------------------------------------------------------- */
/*                            Exportable functions                            */
/* -------------------------------------------------------------------------- */
export const dataToRowTx = (
  t: (key: string, fallback: string) => string,
  address: `0x${string}` | string,
  transfer: Transfer,
  collectionCreator: string,
  theme: MantineTheme
) => [
  <Text key={transfer.timestamp} style={{ fontFamily: 'monument-grotesk-bold' }}>
    {parseAssetIcon(transfer)}
  </Text>,
  <Text key={transfer.timestamp} style={{ fontFamily: 'monument-grotesk-bold' }}>
    {parseAsset(transfer)}
  </Text>,
  <Stack key={transfer.txnHash} style={{ flexDirection: 'row' }}>
    <IconWrapper
      key={transfer.txnHash}
      iconDark={transfer.from === address ? sentIcon : receivedIcon}
      iconLight={transfer.from === address ? sentIcon : receivedIcon}
      style={{ padding: '1px', width: '30px', height: '30px' }}
    />
  </Stack>,
  <UserDetails
    key={transfer.txnHash}
    userId={
      transfer.transferType !== 'TRANSFER' && transfer.token?.collection.id
        ? transfer.token?.collection.id
        : transfer.from === address
          ? transfer.to
          : transfer.from
    }
    width="220px"
  />,
  <Status key={transfer.txnHash} status={transfer.status || 0} />,
  <Stack
    key={transfer.timestamp}
    style={{
      flexDirection: 'row',
      gap: '5px',
      alignItems: 'center',
    }}
  >
    <Text key={transfer.txnHash} style={{ fontFamily: 'monument-grotesk-bold' }}>
      {writeTransferType(transfer)}
    </Text>
    {transfer?.token?.metadata && (
      <>
        <Text style={{ fontFamily: 'monument-grotesk-bold' }}>{transfer.amount}</Text>
        <IconWrapper
          iconDark={itemsIconWhite}
          iconLight={itemsIconBlack}
          style={{ width: '12px', height: '12px' }}
        />
      </>
    )}
  </Stack>,
  <>
    <Text style={{ fontSize: '14px', whiteSpace: 'nowrap', color: theme.colors.textAndIcon[0] }}>
      {formattedDateTime(transfer.timestamp)}
    </Text>
  </>,
]

export const dataToRowActiveStakes = (stake: any, theme: MantineTheme, isMobile: boolean) => [
  <Text key={stake.id} style={{ color: theme.colors.paragraph[0] }}>
    {stake.amount}
  </Text>,
  <Text key={stake.id} style={{ color: theme.colors.paragraph[0] }}>
    {stake.term} days
  </Text>,
  <Text
    key={stake.id}
    style={{ color: theme.colors.paragraph[0], whiteSpace: !isMobile ? 'nowrap' : 'inherit' }}
  >
    {stake.expiry} days remaining
  </Text>,
]

export const nativeCondition = { token_id: { _is_null: true } } // Native transfers
export const flaggedCondition = { token: { flagged: { _eq: false } } } // Non flagged tokens

export const commonConditions = (address: `0x${string}` | string | undefined) => [
  { _or: [{ from: { _eq: address } }, { to: { _eq: address } }] }, // From or to address
  { amount: { _neq: '0' } }, // Non zero amount
]

export const fromAssetFilterToWhereClause = (
  address: `0x${string}` | string | undefined,
  filter: string
) => {
  if (!address) return {}

  switch (filter) {
    case 'All Tokens':
      return {
        _and: [
          ...commonConditions(address),
          { token: { collection: { collection_type: { _eq: 'ERC20' } } } },
          {
            _or: [nativeCondition, flaggedCondition],
          },
        ],
      }
    case 'Items':
      return {
        _and: [
          ...commonConditions(address),
          { token: { collection: { collection_type: { _neq: 'ERC20' } } } }, // Non ERC20 (items)
          flaggedCondition,
        ],
      }
    case 'L1':
      return {
        _and: [
          ...commonConditions(address),
          nativeCondition,
          {
            _or: [
              nativeCondition,
              {
                _and: [
                  { token: { collection: { collection_type: { _eq: 'ERC20' } } } }, // ERC20 tokens
                  flaggedCondition,
                ],
              },
            ],
          },
        ],
      }
    case 'RL1':
      return {
        _and: [
          ...commonConditions(address),
          { token: { collection: { id: { _eq: RL1_ADDRESS.toLowerCase() } } } },
          flaggedCondition,
        ],
      }
    case 'LL1':
      return {
        _and: [
          ...commonConditions(address),
          { token: { collection: { id: { _eq: LL1_ADDRESS.toLowerCase() } } } },
          flaggedCondition,
        ],
      }
    case 'STAKE':
      return {
        _and: [
          ...commonConditions(address),
          { token: { collection: { id: { _eq: STAKE_ADDRESS.toLowerCase() } } } },
          flaggedCondition,
        ],
      }
    case 'AVAX':
      return {
        _and: [
          ...commonConditions(address),
          {
            token: { collection: { id: { _eq: BRIDGE_AVAX_ERC20_REMOTE_ADDRESS.toLowerCase() } } },
          },
          flaggedCondition,
        ],
      }
    default:
      return {
        _and: [
          ...commonConditions(address),
          {
            _or: [nativeCondition, flaggedCondition],
          },
        ],
      }
  }
}

export const fromActivityFilterToWhereClause = (filter: string) => {
  switch (filter) {
    case 'All':
      return {}
    case 'Transferred':
      return { transfer_type: { _eq: 'TRANSFER' } }
    case 'Minted':
      return { transfer_type: { _eq: 'MINT' } }
    case 'Burned':
      return { transfer_type: { _eq: 'BURN' } }
    default:
      return {}
  }
}

export const fromDirectionFilterToWhereClause = (
  address: `0x${string}` | string | undefined,
  filter: string
) => {
  if (!address) return {}

  switch (filter) {
    case 'All':
      return {}
    case 'From Me':
      return { from: { _eq: address } }
    case 'To Me':
      return { to: { _eq: address } }
    default:
      return {}
  }
}

export const fromStatusFilterToWhereClause = (filter: string) => {
  switch (filter) {
    case 'All':
      return {}
    case 'Confirmed':
      return { status: { _eq: 1 } }
    case 'Failed':
      return { status: { _eq: 0 } }
    default:
      return {}
  }
}

export const fromTimestampFilterToWhereClause = (filter: string) => {
  switch (filter) {
    case 'Desc':
      return 'desc'
    case 'Asc':
      return 'asc'
    default:
      return 'desc'
  }
}

export const fromUserFilterToWhereClause = (filter: string) => {
  switch (filter) {
    case 'Users': {
      return {
        _and: [
          {
            from: {
              _nin: [
                STAKE_ADDRESS.toLowerCase(),
                LL1_ADDRESS.toLowerCase(),
                RL1_ADDRESS.toLowerCase(),
                BRIDGE_AVAX_ERC20_REMOTE_ADDRESS.toLowerCase(),
                BRIDGE_L1_NATIVE_HOME_ADDRESS.toLowerCase(),
                REWARDS_ADDRESS.toLowerCase(),
                AIRDROP_CONTRACT_ADDRESS.toLowerCase(),
                CONTRACT_COLLECTOR_ADDRESS.toLowerCase(),
                FAUCET_ADDRESS.toLowerCase(),
              ],
            },
            to: {
              _nin: [
                STAKE_ADDRESS.toLowerCase(),
                LL1_ADDRESS.toLowerCase(),
                RL1_ADDRESS.toLowerCase(),
                BRIDGE_AVAX_ERC20_REMOTE_ADDRESS.toLowerCase(),
                BRIDGE_L1_NATIVE_HOME_ADDRESS.toLowerCase(),
                REWARDS_ADDRESS.toLowerCase(),
                AIRDROP_CONTRACT_ADDRESS.toLowerCase(),
                CONTRACT_COLLECTOR_ADDRESS.toLowerCase(),
                FAUCET_ADDRESS.toLowerCase(),
              ],
            },
          },
          { transfer_type: { _eq: 'TRANSFER' } },
        ],
      }
    }
    case 'Staking':
      return {
        _or: [
          { from: { _eq: STAKE_ADDRESS.toLowerCase() } },
          { to: { _eq: STAKE_ADDRESS.toLowerCase() } },
          {
            _and: [
              { token: { collection_id: { _eq: STAKE_ADDRESS.toLowerCase() } } },
              { transfer_type: { _neq: 'TRANSFER' } },
            ],
          },
        ],
      }
    case 'LL1':
      return {
        _or: [
          { from: { _eq: LL1_ADDRESS.toLowerCase() } },
          { to: { _eq: LL1_ADDRESS.toLowerCase() } },
          {
            _and: [
              { token: { collection_id: { _eq: LL1_ADDRESS.toLowerCase() } } },
              { transfer_type: { _neq: 'TRANSFER' } },
            ],
          },
        ],
      }
    case 'RL1':
      return {
        _or: [
          { from: { _eq: RL1_ADDRESS.toLowerCase() } },
          { to: { _eq: RL1_ADDRESS.toLowerCase() } },
          {
            _and: [
              { token: { collection_id: { _eq: RL1_ADDRESS.toLowerCase() } } },
              { transfer_type: { _neq: 'TRANSFER' } },
            ],
          },
        ],
      }
    case 'AVAX Teleporter':
      return {
        _or: [
          { from: { _eq: BRIDGE_AVAX_ERC20_REMOTE_ADDRESS.toLowerCase() } },
          { to: { _eq: BRIDGE_AVAX_ERC20_REMOTE_ADDRESS.toLowerCase() } },
          {
            _and: [
              { token: { collection_id: { _eq: BRIDGE_AVAX_ERC20_REMOTE_ADDRESS.toLowerCase() } } },
              { transfer_type: { _neq: 'TRANSFER' } },
            ],
          },
        ],
      }
    case 'L1 Teleporter':
      return {
        _or: [
          { from: { _eq: BRIDGE_L1_NATIVE_HOME_ADDRESS.toLowerCase() } },
          { to: { _eq: BRIDGE_L1_NATIVE_HOME_ADDRESS.toLowerCase() } },
        ],
      }
    case 'L1 Identity':
      return {
        _or: [
          { from: { _eq: CONTRACT_COLLECTOR_ADDRESS.toLowerCase() } },
          { to: { _eq: CONTRACT_COLLECTOR_ADDRESS.toLowerCase() } },
        ],
      }
    case 'Faucet':
      return {
        _or: [
          { from: { _eq: FAUCET_ADDRESS.toLowerCase() } },
          { to: { _eq: FAUCET_ADDRESS.toLowerCase() } },
        ],
      }
    case 'Airdrop':
      return {
        _or: [
          { from: { _eq: AIRDROP_CONTRACT_ADDRESS.toLowerCase() } },
          { to: { _eq: AIRDROP_CONTRACT_ADDRESS.toLowerCase() } },
        ],
      }
    default:
      return {}
  }
}

export type SpecialFilters = 'None' | 'Unwrap' | 'Staking' | 'Rewards' | 'StakingPower'

export const fromSpecialsFiltersToWhereClause = (specialFilters: SpecialFilters) => {
  switch (specialFilters) {
    case 'Unwrap':
      return {
        _and: [
          {
            _or: [
              { from: { _eq: RL1_ADDRESS.toLowerCase() } },
              { from: { _eq: LL1_ADDRESS.toLowerCase() } },
            ],
          },
          nativeCondition,
        ],
      }
    case 'Staking':
      return { to: { _eq: STAKE_ADDRESS.toLowerCase() } }
    case 'Rewards':
      return {
        _and: [
          { from: { _eq: STAKE_ADDRESS.toLowerCase() } },
          { token: { collection: { id: { _eq: RL1_ADDRESS.toLowerCase() } } } },
        ],
      }
    case 'StakingPower':
      return {
        _and: [
          { token: { collection: { id: { _eq: STAKE_ADDRESS.toLowerCase() } } } },
          { transfer_type: { _eq: 'MINT' } },
        ],
      }
    default:
      return {}
  }
}
